import { hasAccess, type AccessLevel } from "@woven/contracts";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "../auth/context";
import { LoadingState, PermissionDenied } from "./StatusView";

/**
 * Gate for authenticated console routes. Checks session restore, sign-in
 * state, and minimum access level. Hiding nav items is presentation only —
 * this guard is the actual authorization.
 */
export function ProtectedRoute({
  minimum,
  children,
}: {
  minimum: AccessLevel;
  children: ReactNode;
}) {
  const { user, loading, access, error } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingState label="Restoring session…" />;
  }

  if (!user) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/sign-in" replace state={{ from }} />;
  }

  if (error || access === "guest") {
    return <Navigate to="/sign-up" replace />;
  }

  if (!hasAccess(access, minimum)) {
    return <PermissionDenied needed={minimum} current={access} />;
  }

  return <>{children}</>;
}
